<template>
  <MetaHead :title="wtGeneral('navigation', 'general')" />
  <ProjectHeader :meta="projectBase.projectMeta.value" />
  <div class="relative core-wrapper">
    <StepsIndicator />

    <BaseSection id="details" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
      <h2 class="text-xl">{{ wt('details', 'title') }}: {{ projectBase.projectName.value }}</h2>
      <HelpText slug="project_general"></HelpText>
      <p class="my-4 text-sm text-neutral-550">
        {{ wt('details', 'created') }}
        {{ createDateFormatter(formData?.created as number) }}
      </p>
      <ProjectDetailsForm v-model="formData" />
    </BaseSection>

    <!-- Show only for allowed countries -->
    <BaseSection v-if="showLocationsStep" id="location" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
      <h2 class="text-xl">{{ wt('location', 'title') }}</h2>
      <HelpText slug="project_general_location"></HelpText>
      <SwissMapForm v-model="formData" v-if="formData" />
    </BaseSection>

    <BaseSection id="usages" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
      <h2 class="text-xl">{{ wt('usages', 'title') }}</h2>
      <HelpText slug="project_general_usages"></HelpText>
      <ProjectUsagesForm
        v-if="hasStandardRoles"
        v-model="formData"
        :title="hasMixedRoles ? wt('usages', 'discipline', 'standard') : ''"
        :showUsageItemsError="showUsageItemsError"
        :allowedUsageTypes="STANDARD_USAGE_TYPES"
        @usagesReordered="onUsagesReordered" />
      <ProjectUsagesForm
        v-if="hasLandscapeRole"
        v-model="formData"
        usageItemsKey="landscapeUsageItems"
        :title="hasMixedRoles ? wt('usages', 'discipline', 'landscape') : ''"
        :showUsageItemsError="showLandscapeUsageItemsError"
        :allowedUsageTypes="LANDSCAPE_USAGE_TYPES" />
    </BaseSection>

    <BaseSection
      v-for="role in requirementsTaskRoles"
      :key="role"
      :id="getRequirementsTaskSectionId(role)"
      class="requirements-task-section"
      :nextStep="true"
      @onNextStepClick="projectBase.onNextStepClick">
      <AccordionItem
        class="requirements-task-main-accordion"
        :title="getRequirementsTaskTitle(role)"
        :description="getRequirementsTaskRoleTitle(role)"
        headingTag="h2"
        :open="isRequirementsTaskSectionOpen(role)"
        :controlledOpen="true"
        @toggle="setRequirementsTaskSectionOpen(role, $event)">
        <HelpText :slug="getRequirementsTaskHelpSlug(role)"></HelpText>
        <ProjectDefinitionForm
          v-model="formData"
          v-model:activeAccordionKey="activeRequirementsTaskAccordionKey"
          :role="role"
          :usageItemsKey="getRequirementsTaskUsageItemsKey(role)" />
      </AccordionItem>
    </BaseSection>

    <BaseSection id="area_distribution" :nextStep="true" @onNextStepClick="projectBase.onNextStepClick">
      <h2 class="text-xl">{{ wt('areaDistribution', 'title') }}</h2>
      <HelpText slug="project_general_usage_definition"></HelpText>
      <ProjectAreaDistributionForm
        v-if="hasStandardRoles"
        v-model="formData"
        :showCivilEngineerAreaDistribution="hasCivilEngineerRole"
        @civilEngineerSlabAreaChange="onCivilEngineerSlabAreaChange" />
      <ProjectAreaDistributionForm
        v-if="hasLandscapeRole"
        v-model="formData"
        quantityAreaKey="landscapeQuantityArea"
        usageItemsKey="landscapeUsageItems"
        :quantityTitle="wt('areaDistribution', 'landscape', 'quantity', 'title')"
        :quantityLabel="wt('areaDistribution', 'landscape', 'quantity', 'label')"
        :distributionTitle="wt('areaDistribution', 'landscape', 'distribution', 'title')"
        :showBuiltOverAreaDistribution="true" />
    </BaseSection>

    <!-- Focus Topics Section - only for CH -->
    <BaseSection
      v-if="showFocusTopicsSection"
      id="focus_topics"
      :nextStep="true"
      @onNextStepClick="projectBase.onNextStepClick">
      <h2 class="text-xl">{{ wt('focusTopics', 'title') }}</h2>
      <HelpText slug="project_general_focus_topics"></HelpText>
      <section class="mt-8 boxed-button-grid">
        <label
          v-for="focusTopic in focusTopicsOptions"
          :key="focusTopic"
          class="base-button"
          :class="{ active: hasFocusTopic(focusTopic) }">
          {{ wt('focusTopics', 'option', focusTopic) }}
          <input
            type="checkbox"
            :value="focusTopic"
            :id="focusTopic"
            v-model="formData.focusTopics"
            name="focusTopics" />
        </label>
      </section>
    </BaseSection>

    <BaseSection id="organisation" @onNextModuleClick="onNextModuleClick" nextModule="setup" class="mb-24">
      <h2 class="text-xl">{{ wt('organisation', 'title') }}</h2>
      <HelpText slug="project_general_organisation"></HelpText>
      <ProjectOrganisationForm v-model="formData" />
    </BaseSection>
  </div>
  <ProjectResults :projectName="projectBase.projectName.value" @onSaveProject="onSaveProject"></ProjectResults>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, type Ref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatTimestamp } from '@/helpers/format'
import { deepClone } from '@/helpers/objects_arrays'
import type { NestedObject } from '@/types'
import projectPageBase, { type ValidationConfig } from '@/base/projectPageBase'
import StepsIndicator from '@/components/partials/navigation/StepsIndicator.vue'
import BaseSection from '@/components/partials/layout/BaseSection.vue'
import AccordionItem from '@/components/partials/layout/AccordionItem.vue'
import ProjectDetailsForm from '@/components/project/general/ProjectDetailsForm.vue'
import ProjectOrganisationForm from '@/components/project/general/ProjectOrganisationForm.vue'
import ProjectDefinitionForm from '@/components/project/general/ProjectDefinitionForm.vue'
import ProjectUsagesForm from '@/components/project/general/ProjectUsagesForm.vue'
import ProjectAreaDistributionForm from '@/components/project/general/ProjectAreaDistributionForm.vue'
import ProjectHeader from '@/components/partials/navigation/ProjectHeader.vue'
import HelpText from '@/components/partials/display/HelpText.vue'
import MetaHead from '@/components/partials/display/MetaHead.vue'
import ProjectResults from '@/components/partials/display/ProjectResults.vue'
import SwissMapForm from '@/components/project/general/SwissMapForm.vue'
import {
  countriesWithLocation,
  extend,
  minArrayLength,
  requiredValue,
  requiredValueInCountry,
} from '@/validation/field-validators'
import type { SharedUsageItem, UsageCorrection } from '@/schemas/general/types'
// TEMPORARY: countryHasFocusTopicsOptions unused while focus topics section is hidden
import { BuildingPlan, FocusTopicsOptions /* countryHasFocusTopicsOptions */ } from '@/schemas/general/enums'
import { useClientFeatureFlags } from '@/helpers/client-feature-flags'
import { normalizeUsageBuildingTypeSelections } from '@/helpers/building-type-availability'
import { useWidgetText } from '@/helpers/useWidgetText'
import { useContentStore } from '@/stores/content'
import { DEFAULT_COMPLEXITY_PERCENTAGE } from '@arcexperts/core-types'
import { LANDSCAPE_USAGE_TYPES, STANDARD_USAGE_TYPES } from '@/helpers/usage-types'
import { useProjectStore } from '@/stores/project'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'
import { useSessionAccordionState } from '@/helpers/useSessionAccordionState'
const { wt } = useWidgetText('widget-texts-project-general')
const { wt: wtGeneral } = useWidgetText('widget-texts-general')
const contentStore = useContentStore()
const projectStore = useProjectStore()
const formData: Ref<any> = ref()
const { t } = useI18n()
const showUsageItemsError = ref(false)
const showLandscapeUsageItemsError = ref(false)
const activeRequirementsTaskAccordionKey = ref<string | null>(null)
const REQUIREMENTS_TASK_SECTION_STORAGE_KEY = 'core-widget:project-general:collapsed-requirements-task-sections'
const { isOpen: isRequirementsTaskSectionOpen, setOpen: setRequirementsTaskSectionOpen } = useSessionAccordionState(
  REQUIREMENTS_TASK_SECTION_STORAGE_KEY
)
const createDateFormatter = formatTimestamp
const INTERVENTION_SLIDER_MIN = 0
const INTERVENTION_SLIDER_MAX = 100
const INTERVENTION_SLIDER_STEP = 10

// TEMPORARY: Client feature flags for hiding sections
const { showLocationSection: clientAllowsLocation } = useClientFeatureFlags()

// Derive whether we should show the location section
const showLocationsStep = computed(() => {
  const c = formData.value?.country as string | undefined
  const countryAllows = !!c && countriesWithLocation.includes(c)
  // TEMPORARY: Also check if client allows location section
  return countryAllows && clientAllowsLocation.value
})

// TEMPORARY: Focus topics section hidden globally (VAL-XX)
// To re-enable, restore: return !!c && countryHasFocusTopicsOptions.includes(c)
const showFocusTopicsSection = computed(() => {
  return false
  // Original logic:
  // const c = formData.value?.country as string | undefined
  // return !!c && countryHasFocusTopicsOptions.includes(c)
})

// Focus topics options and helper
const focusTopicsOptions = Object.values(FocusTopicsOptions)
const hasFocusTopic = (focusTopic: FocusTopicsOptions): boolean => {
  return formData.value?.focusTopics?.includes(focusTopic) || false
}

const selectedRoles = computed<string[]>(() => projectStore.getMeta?.roles ?? [])
const requirementsTaskRoles = computed<Role[]>(() => (projectBase.projectValidRoles.value ?? []) as Role[])
const hasLandscapeRole = computed(() => selectedRoles.value.includes('LANDSCAPE_ARCHITECT'))
const hasCivilEngineerRole = computed(() => selectedRoles.value.includes('CIVIL_ENGINEER'))
const hasStandardRoles = computed(() => selectedRoles.value.some((role) => role !== 'LANDSCAPE_ARCHITECT'))
const hasMixedRoles = computed(() => hasLandscapeRole.value && hasStandardRoles.value)

const requirementsTaskRoleTitleFallbacks: Record<string, string> = {
  ARCHITECT: 'SIA 102 Architecture',
  CIVIL_ENGINEER: 'SIA 103 Civil Engineering',
  CIVIL_ENGINEER_EXCAVATION: 'SIA 103 Civil Engineering Excavation',
  ELECTRICAL: 'SIA 108 Electrical Engineering',
  HEATING_COOLING: 'SIA 108 Heating & Cooling',
  VENTILATION_AC: 'SIA 108 Ventilation & Air Conditioning',
  HVAC: 'SIA 108 HVAC',
  SANITARY: 'SIA 108 Sanitary',
  BUILDING_AUTOMATION: 'SIA 108 Building Automation',
  SPECIALIST_COORDINATION: 'SIA 108 Specialist Coordination',
  LANDSCAPE_ARCHITECT: 'SIA 105 Landscape Architecture',
}

function getRequirementsTaskUsageItemsKey(role: Role): 'usageItems' | 'landscapeUsageItems' {
  return role === 'LANDSCAPE_ARCHITECT' ? 'landscapeUsageItems' : 'usageItems'
}

function getRequirementsTaskSectionId(role: Role): string {
  return `requirements_and_tasks_${role.toLowerCase()}`
}

function getRequirementsTaskHelpSlug(role: Role): string {
  return `project_general_task_${role}`
}

function getRequirementsTaskTitle(role: Role): string {
  return (
    contentStore.getWidgetText('widget-texts-project-general', role, 'requirementsAndTasks', 'title', 'option', role) ||
    contentStore.getWidgetText('widget-texts-project-general', role, 'requirementsAndTasks', 'title') ||
    ''
  )
}

function getRequirementsTaskRoleTitle(role: Role): string {
  return (
    contentStore.getWidgetText(
      'widget-texts-project-general',
      role,
      'requirementsAndTasks',
      'subtitle',
      'option',
      role
    ) ||
    requirementsTaskRoleTitleFallbacks[role] ||
    wtGeneral('roleParts', role) ||
    role
  )
}

function requiredForStandardRoles(value: unknown): boolean | string {
  return hasStandardRoles.value ? requiredValue(value) : true
}

function usageItemsForStandardRoles(value: unknown): boolean | string {
  return hasStandardRoles.value ? minArrayLength(1)(value) : true
}

function requiredForLandscapeRole(value: unknown): boolean | string {
  return hasLandscapeRole.value ? requiredValue(value) : true
}

function usageItemsForLandscapeRole(value: unknown): boolean | string {
  return hasLandscapeRole.value ? minArrayLength(1)(value) : true
}

function civilEngineerSlabAreaForRole(value: unknown): boolean | string {
  if (!hasCivilEngineerRole.value) return true
  const required = requiredValue(value)
  if (required !== true) {
    return t('project.general.sections.usage_definition.error.quantity')
  }
  // The total slab area must not be smaller than the floor area (ratio 1:1 is the minimum).
  const quantityArea = Number(formData.value?.quantityArea ?? 0)
  if (typeof value === 'number' && quantityArea > 0 && value < quantityArea) {
    return t('project.general.sections.usage_definition.error.slab_area_min')
  }
  return true
}

// Validation config for general page
const validationConfig: ValidationConfig = {
  schema: {
    canton: requiredValueInCountry,
    quantityArea: extend('project.general.sections.usage_definition.error.quantity', requiredForStandardRoles),
    usageItems: extend('project.general.sections.usage_definition.error.usages', usageItemsForStandardRoles),
    civilEngineerSlabArea: civilEngineerSlabAreaForRole,
    landscapeQuantityArea: extend('project.general.sections.usage_definition.error.quantity', requiredForLandscapeRole),
    landscapeUsageItems: extend('project.general.sections.usage_definition.error.usages', usageItemsForLandscapeRole),
  },
  fieldSections: {
    canton: '#location',
    quantityArea: '#area_distribution',
    usageItems: '#usages',
    civilEngineerSlabArea: '#area_distribution',
    landscapeQuantityArea: '#area_distribution',
    landscapeUsageItems: '#usages',
  },
  sectionErrors: {
    '#location': 'project.general.sections.location.error.default',
    '#usages': 'project.general.sections.usage_definition.error.default',
    '#area_distribution': 'project.general.sections.usage_definition.error.default',
  },
}

const projectBase = projectPageBase.useProjectPageBase(validationConfig)
projectBase.initCalculators()

function remapUsageIndexForMove(index: number, fromIndex: number, toIndex: number): number {
  if (index === fromIndex) return toIndex
  if (fromIndex < toIndex && index > fromIndex && index <= toIndex) return index - 1
  if (fromIndex > toIndex && index >= toIndex && index < fromIndex) return index + 1
  return index
}

function remapUsageCorrectionsForMove(
  corrections: UsageCorrection[] | undefined,
  fromIndex: number,
  toIndex: number
): UsageCorrection[] {
  if (!corrections?.length) return corrections ?? []

  return deepClone(corrections)
    .map((correction) => ({
      ...correction,
      usageIndex: remapUsageIndexForMove(correction.usageIndex, fromIndex, toIndex),
    }))
    .sort((a, b) => a.usageIndex - b.usageIndex)
}

function onUsagesReordered({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }): void {
  if (fromIndex === toIndex) return

  const roleSetupParts = [
    { part: 'architect_setup', calculator: projectBase.calculator.architectCalculator },
    {
      part: 'landscape_architect_setup',
      calculator: projectBase.calculator.landscapeArchitectCalculator,
    },
    { part: 'civil_engineer_setup', calculator: projectBase.calculator.civilEngineerCalculator },
    {
      part: 'civil_engineer_excavation_setup',
      calculator: projectBase.calculator.civilEngineerExcavationCalculator,
    },
    { part: 'engineer_setup', calculator: projectBase.calculator.engineerCalculator },
    { part: 'electrical_setup', calculator: projectBase.calculator.electricalCalculator },
    { part: 'heating_cooling_setup', calculator: projectBase.calculator.heatingCoolingCalculator },
    { part: 'ventilation_ac_setup', calculator: projectBase.calculator.ventilationACCalculator },
    { part: 'hvac_setup', calculator: projectBase.calculator.hvacCalculator },
    { part: 'sanitary_setup', calculator: projectBase.calculator.sanitaryCalculator },
    { part: 'building_automation_setup', calculator: projectBase.calculator.buildingAutomationCalculator },
    {
      part: 'specialist_coordination_setup',
      calculator: projectBase.calculator.specialistCoordinationCalculator,
    },
  ] as const

  const project = projectBase.project.value as Record<string, any> | null

  for (const { part, calculator } of roleSetupParts) {
    const currentSetupData = calculator.setupData as Record<string, unknown> | null | undefined
    if (currentSetupData) {
      const nextCorrections = remapUsageCorrectionsForMove(
        currentSetupData.usageCorrections as UsageCorrection[] | undefined,
        fromIndex,
        toIndex
      )
      calculator.setSetupData({
        ...currentSetupData,
        usageCorrections: nextCorrections,
      } as NestedObject)
    }

    const partData = project?.[part]?.data as Record<string, unknown> | undefined
    if (partData) {
      const nextCorrections = remapUsageCorrectionsForMove(
        partData.usageCorrections as UsageCorrection[] | undefined,
        fromIndex,
        toIndex
      )
      partData.usageCorrections = nextCorrections
    }
  }
}

function ensureUsageTasks(usageItems: unknown[] | undefined): SharedUsageItem[] {
  if (!usageItems?.length) return []

  const DEFAULT_REQUIREMENTS_LEVEL = 'STANDARD'
  const snapInterventionValue = (value: number | undefined) => {
    const fallback = value ?? DEFAULT_COMPLEXITY_PERCENTAGE
    const clamped = Math.min(INTERVENTION_SLIDER_MAX, Math.max(INTERVENTION_SLIDER_MIN, fallback))
    return Math.round(clamped / INTERVENTION_SLIDER_STEP) * INTERVENTION_SLIDER_STEP
  }

  type UsageTaskInput = Partial<SharedUsageItem> & {
    facadeEnvelope?: number
    builtOverAreaRatio?: number | null
    unbuiltAreaRatio?: number | null
    aboveTerrainAreaRatio?: number | null
    belowTerrainAreaRatio?: number | null
  }

  return (usageItems as UsageTaskInput[]).map((item) => ({
    ...item,
    type: item.type!,
    typeCounter: item.typeCounter ?? 0,
    ratio: item.ratio ?? 100,
    requirementsLevel:
      ((item as Partial<SharedUsageItem> & { requirementsLevel?: string }).requirementsLevel as string | undefined) ??
      DEFAULT_REQUIREMENTS_LEVEL,
    buildingPlan: item.buildingPlan ?? BuildingPlan.NEW_BUILDING,
    rawConstruction: snapInterventionValue(item.rawConstruction),
    facadeEnvelope: snapInterventionValue(item.facadeEnvelope ?? item.rawConstruction),
    interiors: snapInterventionValue(item.interiors),
    electricInstallation: snapInterventionValue(item.electricInstallation),
    heatingVentilation: snapInterventionValue(item.heatingVentilation),
    sanitary: snapInterventionValue(item.sanitary),
    builtOverAreaRatio: item.builtOverAreaRatio ?? 0,
    unbuiltAreaRatio: item.unbuiltAreaRatio ?? 100,
    aboveTerrainAreaRatio: item.aboveTerrainAreaRatio ?? 100,
    belowTerrainAreaRatio: item.belowTerrainAreaRatio ?? 0,
  })) as SharedUsageItem[]
}

function initForm(): void {
  const project = projectBase.project.value

  // Try to migrate from existing architect_setup data if general doesn't have usage data yet
  const generalData = project.general.data
  const architectSetupData = project.architect_setup?.data
  const hasGeneralUsages = generalData?.usageItems && (generalData.usageItems as unknown[]).length > 0
  const hasArchitectUsages = architectSetupData?.usageItems && (architectSetupData.usageItems as unknown[]).length > 0

  // Use general data if it has usages, otherwise try to migrate from architect_setup
  let quantityArea = generalData?.quantityArea as number | undefined
  let usageItems = generalData?.usageItems as unknown[] | undefined
  const civilEngineerSlabArea = generalData?.civilEngineerSlabArea as number | null | undefined
  const landscapeQuantityArea = generalData?.landscapeQuantityArea as number | undefined
  const landscapeUsageItems = generalData?.landscapeUsageItems as unknown[] | undefined

  if (!hasGeneralUsages && hasArchitectUsages) {
    // Migrate from architect_setup
    quantityArea = architectSetupData.quantityArea as number
    // Convert UsageItem[] to SharedUsageItem[] (strip role-specific fields)
    usageItems = (architectSetupData.usageItems as { type: string; typeCounter: number; ratio: number }[]).map(
      (item) => ({
        type: item.type,
        typeCounter: item.typeCounter ?? 0,
        ratio: item.ratio,
      })
    )
  }

  const preparedUsageItems = ensureUsageTasks(usageItems)
  const normalizedUsageItems = normalizeUsageBuildingTypeSelections(
    preparedUsageItems as Array<{
      type?: string | null
      buildingTypeId?: string | null
      buildingTypeName?: string | null
    }>,
    generalData?.country,
    contentStore.getBuildingTypes
  ).usageItems
  const preparedLandscapeUsageItems = ensureUsageTasks(landscapeUsageItems)
  const normalizedLandscapeUsageItems = normalizeUsageBuildingTypeSelections(
    preparedLandscapeUsageItems as Array<{
      type?: string | null
      buildingTypeId?: string | null
      buildingTypeName?: string | null
    }>,
    generalData?.country,
    contentStore.getBuildingTypes
  ).usageItems

  formData.value = {
    ...project.general.data,
    // Ensure usageItems and quantityArea are initialized (default 100 m²)
    quantityArea: quantityArea ?? 100,
    usageItems: normalizedUsageItems,
    // The total slab area defaults to the floor area (ratio 1:1) until the user adjusts it.
    civilEngineerSlabArea: hasCivilEngineerRole.value ? (civilEngineerSlabArea ?? quantityArea ?? 100) : null,
    landscapeQuantityArea: landscapeQuantityArea ?? 100,
    landscapeUsageItems: normalizedLandscapeUsageItems,
  }

  projectBase.validator.initForm(formData.value, projectBase.validationSchema)
}

function onCivilEngineerSlabAreaChange(value: number | null): void {
  if (!formData.value) return
  formData.value.civilEngineerSlabArea = value
  projectBase.calculator.setProjectGeneralData(formData.value)
}

function normalizeFormUsageBuildingTypes(): void {
  if (!formData.value?.usageItems) return

  const { changed: standardChanged, usageItems } = normalizeUsageBuildingTypeSelections(
    formData.value.usageItems as Array<{
      type?: string | null
      buildingTypeId?: string | null
      buildingTypeName?: string | null
    }>,
    formData.value.country,
    contentStore.getBuildingTypes
  )
  const { changed: landscapeChanged, usageItems: landscapeUsageItems } = normalizeUsageBuildingTypeSelections(
    formData.value.landscapeUsageItems as Array<{
      type?: string | null
      buildingTypeId?: string | null
      buildingTypeName?: string | null
    }>,
    formData.value.country,
    contentStore.getBuildingTypes
  )

  if (!standardChanged && !landscapeChanged) return

  formData.value = {
    ...formData.value,
    ...(standardChanged ? { usageItems } : {}),
    ...(landscapeChanged ? { landscapeUsageItems } : {}),
  }
}

async function onSaveProject(): Promise<void> {
  if (!formData.value) return
  showUsageItemsError.value = true
  showLandscapeUsageItemsError.value = true
  const success = await projectBase.saveProjectPart('general', formData.value)
  if (success) {
    showUsageItemsError.value = false
    showLandscapeUsageItemsError.value = false
    initForm()
  }
}

async function onNextModuleClick(): Promise<void> {
  showUsageItemsError.value = true
  showLandscapeUsageItemsError.value = true
  await projectBase.onNextModuleClick('general', formData.value, 'setup-project-view')
}

onMounted(() => {
  initForm()
})

onBeforeRouteLeave(async (to, from, next) => {
  await projectBase.checkFormIsDirty(next, { type: 'general', data: formData.value })
})

watch(
  formData,
  () => {
    projectBase.validator.onFormValuesChange(formData.value as NestedObject)
    projectBase.calculator.setProjectGeneralData(formData.value)
  },
  { deep: true, immediate: false }
)

watch([() => formData.value?.country, () => contentStore.buildingTypes], () => {
  normalizeFormUsageBuildingTypes()
})

// VAL-153: while the total slab area still equals the floor area (prefilled 1:1 state),
// a floor area change moves it along. A user-entered value is kept and only validated.
watch(
  () => formData.value?.quantityArea as number | undefined,
  (next, previous) => {
    if (!formData.value || !hasCivilEngineerRole.value) return
    if (typeof next !== 'number' || !(next > 0) || typeof previous !== 'number') return
    if (formData.value.civilEngineerSlabArea !== previous) return
    formData.value.civilEngineerSlabArea = next
  }
)

watch(
  () => formData.value?.usageItems,
  () => {
    showUsageItemsError.value = false
  },
  { deep: true }
)

watch(
  () => formData.value?.landscapeUsageItems,
  () => {
    showLandscapeUsageItemsError.value = false
  },
  { deep: true }
)
</script>

<style lang="postcss" scoped>
.requirements-task-section :deep(.requirements-task-main-accordion) {
  @apply border-b-0 py-0;
}

.requirements-task-section :deep(.requirements-task-main-accordion .core-accordion-header h2) {
  @apply text-xl;
}

.requirements-task-section :deep(.requirements-task-main-accordion .core-accordion-header p) {
  @apply mt-1 text-lg;
}
</style>
